import * as React from "react";
import { useCallback } from "react";
import styled, { css } from "styled-components";
import { Formik, Form, useField } from "formik";
import moment from "moment/moment";
import useUserDateTime from "hooks/useUserDateTime";
import { ModalSubmit, Icon } from "components/common";
import { Checkbox } from "components/bootstrap";
import { isValidDate, toUTCFromTz, adjustFormat } from "util/DateTime";
import {
  DATE_SEPARATOR,
  extractRangeFromString,
  timeRangeTitle
} from "components/common/EntityFilters/helpers/timeRange";
import DateTimePicker from "views/components/time-range-picker/DateTimePicker";
import StringUtils from "util/StringUtils";
const Container = styled.div`
  padding: 3px 10px;
  max-width: fit-content;
`;
const Info = styled.p(
  ({ theme }) => css`
    font-size: ${theme.fonts.size.small};
    margin: 0 0 10px;
  `
);
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 3px;
`;
const StyledLabel = styled.label`
  margin: 0;
`;
const StyledCheckbox = styled(Checkbox)`
  &.checkbox {
    margin: 0;
  }
`;
const useInitialValues = (filter) => {
  const { formatTime } = useUserDateTime();
  if (filter) {
    const [from, until] = extractRangeFromString(filter.value);
    return {
      from: from ? formatTime(from, "complete") : void 0,
      until: until ? formatTime(until, "complete") : void 0
    };
  }
  return {
    from: formatTime(moment().subtract(5, "minutes"), "complete"),
    until: void 0
  };
};
const formatError = "Format must be: YYYY-MM-DD [HH:mm:ss[.SSS]].";
const rangeError = 'The "Until" date must come after the "From" date.';
const validate = (values) => {
  let errors = {};
  if (values.from && !isValidDate(values.from)) {
    errors = { ...errors, from: formatError };
  }
  if (values.until && !isValidDate(values.until)) {
    errors = { ...errors, until: formatError };
  }
  if (values.from >= values.until) {
    errors = { ...errors, until: rangeError };
  }
  return errors;
};
const PickerContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 10px;
`;
const PickerWrap = styled.div`
  max-width: 295px;
`;
const Picker = ({ name }) => {
  const { formatTime } = useUserDateTime();
  const label = StringUtils.capitalizeFirstLetter(name);
  const [{ onChange, value }, meta] = useField(name);
  const _onChange = useCallback(
    (newValue) => onChange({ target: { name, value: newValue } }),
    [onChange, name]
  );
  const onChangeAllTime = () => _onChange(value ? void 0 : formatTime(/* @__PURE__ */ new Date(), "complete"));
  const checkboxLabel = name === "from" ? "All Time" : "Now";
  const isChecked = !value;
  return /* @__PURE__ */ React.createElement(PickerWrap, { "data-testid": `date-picker-${name}` }, /* @__PURE__ */ React.createElement(SectionHeader, null, /* @__PURE__ */ React.createElement(StyledLabel, { htmlFor: `date-input-${name}` }, label), /* @__PURE__ */ React.createElement(StyledCheckbox, { onChange: onChangeAllTime, checked: isChecked }, checkboxLabel)), /* @__PURE__ */ React.createElement(DateTimePicker, { disabled: isChecked, error: meta.error, onChange: _onChange, value, range: label }));
};
const FromPicker = () => /* @__PURE__ */ React.createElement(Picker, { name: "from" });
const UntilPicker = () => /* @__PURE__ */ React.createElement(Picker, { name: "until" });
const DateRangeForm = ({ filter, onSubmit }) => {
  const { userTimezone } = useUserDateTime();
  const initialValues = useInitialValues(filter);
  const _onSubmit = (formValues) => {
    const toInternalTime = (date) => adjustFormat(toUTCFromTz(date, userTimezone), "internal");
    const utcFrom = formValues.from ? toInternalTime(formValues.from) : "";
    const utcUntil = formValues.until ? toInternalTime(formValues.until) : "";
    onSubmit({
      title: timeRangeTitle(formValues.from, formValues.until),
      value: `${utcFrom}${DATE_SEPARATOR}${utcUntil}`
    });
  };
  return /* @__PURE__ */ React.createElement(Container, { "data-testid": "time-range-form" }, /* @__PURE__ */ React.createElement(Formik, { initialValues, onSubmit: _onSubmit, validate }, ({ isValid }) => /* @__PURE__ */ React.createElement(Form, null, /* @__PURE__ */ React.createElement(PickerContainer, null, /* @__PURE__ */ React.createElement(FromPicker, null), /* @__PURE__ */ React.createElement(Icon, { name: "arrow_right_alt" }), /* @__PURE__ */ React.createElement(UntilPicker, null)), /* @__PURE__ */ React.createElement(Info, null, "All timezones using: ", /* @__PURE__ */ React.createElement("b", null, userTimezone), "."), /* @__PURE__ */ React.createElement(
    ModalSubmit,
    {
      submitButtonText: `${filter ? "Update" : "Create"} filter`,
      bsSize: "small",
      disabledSubmit: !isValid,
      displayCancel: false
    }
  ))));
};
export default DateRangeForm;
